/**
 * ADWG comparison table — customer-facing "Parameters vs ADWG" section.
 *
 * Pulls live evaluation rows from the same backend SSoT (`adwg_comparison` on
 * the report payload, or a fresh call to `/api/adwg/guidelines`).
 *
 * By default this is the SUMMARY-style "Parameters vs ADWG" panel and only
 * surfaces rows that are outside the relevant guideline/preferred range —
 * statuses `attention` and `fail` (plus microbiological positives, which
 * already map to `fail`/`attention` in the evaluator). Rows with status
 * `pass`, `untested` or `info` are hidden.
 *
 * Pass `mode="full"` to render every row (used by the COA / full results
 * table where customers expect to see all tested parameters).
 *
 * Each row shows: Parameter (+ basis badge) · Result · Unit · ADWG Guideline · Status pill.
 * Aware internal-threshold transparency notes are surfaced below the table.
 */
import { ShieldCheck, Droplet, Wrench, Info, Check, FileText } from "lucide-react";

const BASIS = {
  health:        { label: "Health",        bg: "bg-rose-100",   fg: "text-rose-700",   icon: ShieldCheck },
  aesthetic:     { label: "Aesthetic",     bg: "bg-cyan-100",   fg: "text-cyan-700",   icon: Droplet },
  operational:   { label: "Operational",   bg: "bg-violet-100", fg: "text-violet-700", icon: Wrench },
  informational: { label: "Informational", bg: "bg-slate-100",  fg: "text-slate-600",  icon: Info },
};

const STATUS = {
  pass:      { bg: "bg-emerald-500", fg: "text-white",  label: "PASS" },
  attention: { bg: "bg-amber-500",   fg: "text-white",  label: "ATTENTION" },
  fail:      { bg: "bg-rose-600",    fg: "text-white",  label: "FAIL" },
  info:      { bg: "bg-slate-400",   fg: "text-white",  label: "INFO" },
  untested:  { bg: "bg-slate-200",   fg: "text-slate-600", label: "NOT TESTED" },
};

// Which row statuses are considered "flagged outside the relevant guideline"
// and therefore belong in the Summary Report's "Parameters vs ADWG" section.
const FLAGGED_STATUSES = new Set(["attention", "fail"]);


export function AdwgComparisonTable({ rows = [], compact = false, mode = "summary", coaHref = null, dataTestId = "adwg-table" }) {
  if (!rows || rows.length === 0) return null;
  const isFull = mode === "full";
  const visibleRows = isFull ? rows : rows.filter((r) => FLAGGED_STATUSES.has(r.status));
  const internalNotes = visibleRows.filter((r) => r.internal_note);

  // Empty-state for the summary mode — no flagged parameters means the
  // customer should see a short positive message rather than an empty table.
  if (!isFull && visibleRows.length === 0) {
    return (
      <div data-testid={dataTestId} className="rounded-3xl bg-white border border-slate-200 overflow-hidden">
        <div className="px-6 py-5 border-b border-slate-100 bg-slate-50/60">
          <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-cyan-700 flex items-center gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5" />
            Australian Drinking Water Guidelines
          </div>
          <h3 className="font-display text-lg lg:text-xl font-bold text-slate-900 mt-1.5">Parameter Results vs Australian Drinking Water Guidelines</h3>
        </div>
        <div data-testid="adwg-empty-state" className="px-6 py-7 flex items-start gap-3 text-emerald-800 bg-emerald-50/60">
          <Check className="w-5 h-5 mt-0.5 text-emerald-600 flex-shrink-0" />
          <p className="text-sm leading-relaxed">
            <strong>All tested parameters were within range.</strong> No tested parameters were flagged outside
            the applicable guideline or review range for this sample.
          </p>
        </div>
        <CoaFooter coaHref={coaHref} />
      </div>
    );
  }

  return (
    <div data-testid={dataTestId} className="rounded-3xl bg-white border border-slate-200 overflow-hidden">
      <div className="px-6 py-5 border-b border-slate-100 bg-slate-50/60">
        <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-cyan-700 flex items-center gap-1.5">
          <ShieldCheck className="w-3.5 h-3.5" />
          Australian Drinking Water Guidelines
        </div>
        <h3 className="font-display text-lg lg:text-xl font-bold text-slate-900 mt-1.5">
          Parameter Results vs Australian Drinking Water Guidelines
        </h3>
        <p className="mt-1 text-xs text-slate-500 leading-relaxed">
          {isFull
            ? "Each parameter is classified as Health, Aesthetic, Operational or Informational and compared against the current Australian Drinking Water Guideline."
            : "Only parameters flagged outside the applicable ADWG / preferred range are listed here. Refer to the Official COA for the complete parameter panel for this test type."}
        </p>
      </div>

      {/* Desktop table */}
      <div className="hidden md:block">
        <table className="w-full text-sm">
          <thead className="bg-slate-900 text-white">
            <tr>
              <th className="text-left px-5 py-3 font-semibold">Parameter</th>
              <th className="text-left px-5 py-3 font-semibold">Result</th>
              {!compact && <th className="text-left px-5 py-3 font-semibold">Unit</th>}
              <th className="text-left px-5 py-3 font-semibold">ADWG Guideline</th>
              <th className="text-center px-5 py-3 font-semibold">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {visibleRows.map((r) => {
              const basis = BASIS[r.basis] || BASIS.informational;
              const status = STATUS[r.status] || STATUS.untested;
              const Icon = basis.icon;
              return (
                <tr key={r.key} className="even:bg-slate-50/50" data-testid={`adwg-row-${r.key}`}>
                  <td className="px-5 py-3.5">
                    <div className="font-semibold text-slate-900">{r.display_name}</div>
                    <div className={`inline-flex items-center gap-1 mt-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${basis.bg} ${basis.fg}`}>
                      <Icon className="w-3 h-3" /> {basis.label}
                    </div>
                  </td>
                  <td className="px-5 py-3.5 text-slate-900 tabular-nums">{r.result_display}</td>
                  {!compact && <td className="px-5 py-3.5 text-slate-500 text-xs">{r.unit || "—"}</td>}
                  <td className="px-5 py-3.5 text-slate-700">{r.guideline_text}</td>
                  <td className="px-5 py-3.5 text-center">
                    <span className={`inline-block px-2.5 py-1 rounded-full text-[10px] font-bold tracking-wider ${status.bg} ${status.fg}`}>
                      {r.status_label || status.label}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile card list */}
      <div className="md:hidden divide-y divide-slate-100">
        {visibleRows.map((r) => {
          const basis = BASIS[r.basis] || BASIS.informational;
          const status = STATUS[r.status] || STATUS.untested;
          const Icon = basis.icon;
          return (
            <div key={r.key} className="px-5 py-4" data-testid={`adwg-row-${r.key}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-semibold text-slate-900">{r.display_name}</div>
                  <div className={`inline-flex items-center gap-1 mt-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${basis.bg} ${basis.fg}`}>
                    <Icon className="w-3 h-3" /> {basis.label}
                  </div>
                </div>
                <span className={`inline-block px-2.5 py-1 rounded-full text-[10px] font-bold tracking-wider ${status.bg} ${status.fg}`}>
                  {r.status_label || status.label}
                </span>
              </div>
              <div className="mt-2.5 grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Result</div>
                  <div className="text-slate-900 tabular-nums">{r.result_display} <span className="text-xs text-slate-500">{r.unit}</span></div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">ADWG</div>
                  <div className="text-slate-700">{r.guideline_text}</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {internalNotes.length > 0 && (
        <div className="px-6 py-4 bg-slate-50 border-t border-slate-200" data-testid="adwg-transparency">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-600 mb-2">Transparency note</div>
          <ul className="space-y-1.5 text-xs text-slate-600 leading-relaxed">
            {internalNotes.map((r) => (
              <li key={r.key}>
                <span className="font-semibold text-slate-800">{r.display_name}:</span> {r.internal_note}
              </li>
            ))}
          </ul>
        </div>
      )}
      <CoaFooter coaHref={coaHref} />
    </div>
  );
}

/**
 * Subtle footer beneath the flagged-only ADWG section that points customers
 * at the full Official COA panel. Renders only in summary mode (the
 * standalone full-panel render does not need a self-link). Falls back to
 * plain helper wording if no COA download URL is wired in.
 */
function CoaFooter({ coaHref }) {
  return (
    <div data-testid="adwg-view-coa-footer" className="px-6 py-3 border-t border-slate-100 bg-white text-xs text-slate-500 flex items-center justify-between gap-3 flex-wrap">
      <span>Only flagged results are shown here. The full parameter panel is included in the Official COA.</span>
      {coaHref ? (
        <a
          href={coaHref}
          target={coaHref.startsWith("http") || coaHref.endsWith(".pdf") ? "_blank" : undefined}
          rel="noreferrer"
          data-testid="adwg-view-coa-link"
          className="inline-flex items-center gap-1.5 text-cyan-700 hover:text-cyan-900 font-semibold underline underline-offset-2 hover:no-underline"
        >
          <FileText className="w-3.5 h-3.5" /> View Official COA
        </a>
      ) : null}
    </div>
  );
}
